import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Box } from '@atlaskit/primitives/compiled';
import { expVal } from '@atlassian/jira-feature-experiments';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import { CATEGORY_TYPE, SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useBespokeEmptyFieldTextGA } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-ga/index.tsx';
import { useIsJsmIssue } from '@atlassian/jira-issue-util-hooks/src/index.tsx';
import messages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { singleSelect_issueFieldSingleSelectReadviewFull_SingleSelectReadView$key as SingleSelectReadViewFragment } from './__generated__/singleSelect_issueFieldSingleSelectReadviewFull_SingleSelectReadView.graphql';
import type { SingleSelectReadViewProps } from './types.tsx';
/**
 * The SingleSelectReadView will show a read only view of SingleSelect field.
 * @param props {@link SingleSelectReadViewProps}
 */
export const SingleSelectReadView = (props: SingleSelectReadViewProps): React.JSX.Element => {
	const { fragmentRef, fieldType, shouldShowPopover } = props;
	const isJsmIssue = useIsJsmIssue();
	const data = useFragment<SingleSelectReadViewFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment singleSelect_issueFieldSingleSelectReadviewFull_SingleSelectReadView on JiraSingleSelectField {
				id
				fieldOption {
					value
					color {
						colorKey
					}
				}
			}
		`,
		fragmentRef,
	);

	const hoverPopoverRefFactory = shouldShowPopover
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: data.fieldOption?.value,
		});
	}

	const isBespokeEmptyFieldTextGA = useBespokeEmptyFieldTextGA();

	if (!data.fieldOption?.value) {
		return (
			<EmptyFieldText
				{...(isBespokeEmptyFieldTextGA && {
					placeholderText: messages.bespokenSingleSelectPlaceholderEmptyFieldText,
				})}
			/>
		);
	}

	const isCategoryAndListExperimentON = props.fieldType === CATEGORY_TYPE;
	// Separate checks to assure exposure
	const isSingleSelectAndColourfulExperimentOn =
		fieldType === SELECT_CF_TYPE &&
		expVal(
			'thor_colourful_single_select_milestone2_experiment',
			'isColorfulSingleSelectEnabled',
			false,
		);

	if (isSingleSelectAndColourfulExperimentOn || isCategoryAndListExperimentON) {
		if (shouldShowPopover) {
			return (
				<OptionTag
					name={data.fieldOption?.value ?? ''}
					color={data.fieldOption?.color?.colorKey ?? null}
					ref={hoverPopoverRef}
				/>
			);
		}
		return (
			<OptionTag
				name={data.fieldOption.value}
				color={data?.fieldOption?.color?.colorKey ?? null}
				showTooltip={
					fg('add_tooltip_to_single_select_options') ||
					(!isJsmIssue && expVal('single_select_with_tooltips', 'enabled', false))
				}
			/>
		);
	}

	return shouldShowPopover ? (
		<Box ref={hoverPopoverRef}>{data.fieldOption?.value ?? ''}</Box>
	) : (
		<>{data.fieldOption?.value ?? ''}</>
	);
};
